import 'src/styles/ui/blend-check.css';

import {
  faBan,
  faCheck,
  faChevronDown,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCodeMerge,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import BlendCardSide from 'src/components/blend-check/BlendCardSide';
import { BLEND_ATTRIBUTE_ICONS } from 'src/components/blend-check/blendCheckIcons';
import BlendReviewScreen from 'src/components/blend-check/BlendReviewScreen';
import Loader from 'src/components/loader';
import Button from 'src/components/ui/Button';
import NoticeCard from 'src/components/ui/NoticeCard';
import { BLEND_CHECK_MODE } from 'src/hooks/useBlendCheck';
import {
  BLEND_ATTRIBUTE,
  getAttributeSummary,
  getPowderQualityLabel,
} from 'src/utils/blendAttributes';
import { pluralWord } from 'src/utils/stringUtils';

const ROW_STATUS = { BLOCKED: 'blocked', DIFFERS: 'differs', MATCH: 'match' };

const ROW_STATUS_ICON = {
  [ROW_STATUS.BLOCKED]: faBan,
  [ROW_STATUS.DIFFERS]: faCircleExclamation,
  [ROW_STATUS.MATCH]: faCircleCheck,
};

/** Compact value for the summary rows: counts for lots, real values elsewhere. */
const getSummaryValue = (attribute, side) => {
  const value = attribute[side];

  switch (attribute.key) {
    case BLEND_ATTRIBUTE.MATERIAL_TYPE:
      return (value || []).map(material => material.name).join(', ');
    case BLEND_ATTRIBUTE.MATERIAL_LOT: {
      const count = (value || []).length;
      return `${count} ${pluralWord('lot', count)}`;
    }
    case BLEND_ATTRIBUTE.POWDER_QUALITY:
      return getPowderQualityLabel(value);
    default:
      return `${value ?? ''}`;
  }
};

/**
 * Blended-batch summary: there is only one side, so a two-column table would be
 * half empty. Label left, value right, in normal case.
 */
const BlendSingleRows = ({ check }) => (
  <div className='notice-card__body'>
    {check.attributes.map(attribute => {
      const mixed = attribute.differs;

      return (
        <div
          key={attribute.key}
          className={classNames(
            'blend-summary-row',
            mixed ? 'blend-summary-row--mixed' : 'blend-summary-row--single'
          )}
        >
          <FontAwesomeIcon icon={mixed ? faCircleExclamation : faCircleCheck} />
          <span className='blend-summary-row-name'>{attribute.label}</span>
          <span className='blend-summary-row-value'>{getAttributeSummary(attribute)}</span>
        </div>
      );
    })}
  </div>
);

// Comparison summary: the values behind the expander
const BlendComparisonRows = ({ check }) => (
  <div className='notice-card__body'>
    <div className='blend-compare__head'>
      <BlendCardSide variant='source' caption='Source' resource={check.source} />
      <BlendCardSide variant='destination' caption='Destination' resource={check.destination} />
    </div>

    {check.attributes.map(attribute => {
      const acknowledged = check.isAcknowledged(attribute.key);
      const isBlockedRow =
        check.isBlocked && attribute.key === BLEND_ATTRIBUTE.MATERIAL_TYPE && attribute.differs;

      let status = ROW_STATUS.MATCH;
      if (isBlockedRow) {
        status = ROW_STATUS.BLOCKED;
      } else if (attribute.differs && !acknowledged) {
        status = ROW_STATUS.DIFFERS;
      }

      return (
        <div
          key={attribute.key}
          className={classNames('blend-row', {
            'blend-row--blocked': status === ROW_STATUS.BLOCKED,
            'blend-row--differs': status === ROW_STATUS.DIFFERS,
          })}
        >
          <div className='blend-row__label'>
            <FontAwesomeIcon
              className='blend-row__icon'
              icon={BLEND_ATTRIBUTE_ICONS[attribute.key] || faCircleInfo}
            />
            <span className='blend-row__name'>{attribute.label}</span>
            <FontAwesomeIcon icon={ROW_STATUS_ICON[status]} className='blend-row__status' />
          </div>
          <div className='blend-row__values'>
            <div className='blend-row__value'>{getSummaryValue(attribute, 'source')}</div>
            <div className='blend-row__value'>{getSummaryValue(attribute, 'destination')}</div>
          </div>
        </div>
      );
    })}
  </div>
);

/**
 * The warning, the all-match reassurance and the feature-flag hard block, in one
 * card. Drop it on any page that blends material, above the action controls.
 *
 * @param {object} check           result of useBlendCheck
 * @param {node}   [blockedAction] optional way out, rendered inside the blocked card
 * @param {boolean} [actionBlocked] the page already blocks the action for another
 *   reason, so this card must not present a second verdict
 */
const BlendCheckGate = ({ check, blockedAction, actionBlocked, className }) => {
  const [isExpanded, setExpanded] = useState(false);
  const [isDoneExpanded, setDoneExpanded] = useState(false);
  const toggleExpanded = () => setExpanded(open => !open);

  // Holds the space and says so, rather than letting a card drop in under the
  // operator's thumb once the batches land.
  if (check.isChecking) {
    return (
      <div className={className}>
        <div className='blend-checking'>
          <Loader inline showText={false} />
          <span>Checking for differences…</span>
        </div>
      </div>
    );
  }

  if (check.mode === BLEND_CHECK_MODE.NONE) {
    return null;
  }

  const isBlendedBatch = check.mode === BLEND_CHECK_MODE.BLENDED_BATCH;
  const { differences, attributes, isBlocked, actionLabel } = check;

  const wrapContent = content => <div className={className}>{content}</div>;

  if (isBlocked) {
    // A second "Cannot ..." under a block the page already shows reads as two
    // verdicts on one dead action, and hiding either would only reveal it after the
    // operator acts on the other. So both stay, and this one states its reason
    // instead of claiming the verdict for itself.
    return wrapContent(
      <NoticeCard
        variant='danger'
        icon={faBan}
        tile='notice-tile--danger'
        title={actionBlocked ? 'Material types do not match' : `Cannot ${actionLabel}`}
        sub={actionBlocked ? null : 'Material types do not match.'}
        moreLabel='Why this is blocked'
        isOpen={isExpanded}
        action={blockedAction}
        onToggle={toggleExpanded}
      >
        <BlendComparisonRows check={check} />
        <div className='notice-card__note'>
          This {actionLabel} is blocked because the batches contain different material types.
          Blending across different material types is not allowed for this bureau.
        </div>
      </NoticeCard>
    );
  }

  if (!differences.length) {
    return wrapContent(
      <NoticeCard
        variant='ok'
        icon={faCheck}
        tile='notice-tile--ok'
        title='Materials match'
        moreLabel='What was checked'
        isOpen={isExpanded}
        onToggle={toggleExpanded}
      >
        {isBlendedBatch ? <BlendSingleRows check={check} /> : <BlendComparisonRows check={check} />}
      </NoticeCard>
    );
  }

  // Everything acknowledged: collapse to one green line that expands on demand.
  if (check.isReviewComplete) {
    return wrapContent(
      <div
        className={classNames('notice-card', 'notice-card--ok', {
          'blend-done--open': isDoneExpanded,
        })}
      >
        <button type='button' className='blend-done' onClick={() => setDoneExpanded(open => !open)}>
          <span className='blend-done__icon'>
            <FontAwesomeIcon icon={faCheck} />
          </span>
          <span className='blend-done__text'>
            {differences.length} {pluralWord('difference', differences.length)} acknowledged
          </span>
          <span className='blend-done__action'>
            Details
            <FontAwesomeIcon icon={faChevronDown} />
          </span>
        </button>
        {isDoneExpanded && (
          <div className='blend-done__list'>
            {attributes.map(attribute => (
              <div key={attribute.key} className='blend-done__row'>
                <FontAwesomeIcon icon={faCheck} />
                <span className='blend-done__row-name'>{attribute.label}</span>
                <span className='blend-done__row-value'>
                  {attribute.differs ? 'Acknowledged' : 'Match'}
                </span>
              </div>
            ))}
            <div className='blend-done__footer'>
              <Button block variant='ghost' size='small' onClick={check.openReview}>
                Review again
              </Button>
            </div>
          </div>
        )}
        <BlendReviewScreen check={check} />
      </div>
    );
  }

  const title = isBlendedBatch
    ? 'This batch is a blend'
    : `${differences.length} of ${attributes.length} attributes differ`;
  const sub = isBlendedBatch
    ? 'It was made by mixing material together. Check what is inside before loading it.'
    : 'Acknowledge each difference to continue.';
  const cta = isBlendedBatch ? 'See what is inside' : 'Review differences';

  return wrapContent(
    <>
      <NoticeCard
        icon={isBlendedBatch ? faCodeMerge : faTriangleExclamation}
        tile='notice-tile--warn'
        title={title}
        sub={sub}
        moreMeta={
          check.acknowledgedCount > 0
            ? `${check.acknowledgedCount} of ${differences.length} done`
            : null
        }
        moreLabel={isBlendedBatch ? 'What is inside' : 'What needs review'}
        isOpen={isExpanded}
        action={
          <Button fill block variant='warning' onClick={check.openReview}>
            {check.acknowledgedCount > 0 ? 'Continue review' : cta}
          </Button>
        }
        onToggle={toggleExpanded}
      >
        {isBlendedBatch ? <BlendSingleRows check={check} /> : <BlendComparisonRows check={check} />}
      </NoticeCard>
      <BlendReviewScreen check={check} />
    </>
  );
};

BlendSingleRows.propTypes = { check: PropTypes.object.isRequired };
BlendComparisonRows.propTypes = { check: PropTypes.object.isRequired };

BlendCheckGate.propTypes = {
  check: PropTypes.object.isRequired,
  blockedAction: PropTypes.node,
  actionBlocked: PropTypes.bool,
  className: PropTypes.string,
};

BlendCheckGate.defaultProps = {
  blockedAction: null,
  actionBlocked: false,
  className: 'mb-4',
};

export default BlendCheckGate;
